import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { connectBankAccount, createStripeDashboardLink, fetchFinanceAccount } from '../../store/actions/FinanceActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import {
  PiBankLight,
  PiArrowSquareOutLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiShieldCheckLight,
  PiCurrencyDollarLight,
  PiClockLight,
  PiGearLight,
  PiInfoLight,
} from 'react-icons/pi';

interface Props {
  organizationId: number | undefined;
}

const BankAccountTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const account = useAppSelector((state: RootState) => state.finance.account);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);
  const preferredCurrency = useAppSelector((state: RootState) => state.user?.userInfo?.preferences?.general_settings?.currency) || 'usd';
  const balance = useAppSelector((state: RootState) => state.finance.balance);
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (!organizationId) return;
    setConnecting(true);

    const result = await dispatch(
      connectBankAccount({
        organization_id: organizationId,
        email: userInfo?.email,
        business_name: userInfo?.organization_name,
        country: 'US',
        refresh_url: `${window.location.origin}/finance`,
        return_url: `${window.location.origin}/finance`,
      })
    );

    if (result?.onboarding_url) {
      window.location.href = result.onboarding_url;
    }
    setConnecting(false);
  };

  const handleOpenDashboard = async () => {
    if (!organizationId) return;
    const result = await dispatch(createStripeDashboardLink(organizationId));
    if (result?.url) {
      window.open(result.url, '_blank');
    }
  };

  const handleRefresh = () => {
    if (organizationId) {
      dispatch(fetchFinanceAccount(organizationId));
    }
  };

  // No account connected
  if (!account) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm p-8 text-center">
          <div className="text-black dark:text-white mb-4">
            <PiBankLight className="mx-auto h-10 w-10" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('Connect Your Bank Account')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('Connect your organization\'s bank account through Stripe to receive client payments and manage payouts — all within PONS.')}
          </p>
          <Button
            variant="primary"
            size="medium"
            onClick={handleConnect}
            isLoading={connecting || loading?.connect}
          >
            {t('Connect Bank Account')}
          </Button>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
            {t('Powered by Stripe. Your banking information is securely handled by Stripe — we never see your full account number.')}
          </p>
        </div>
      </div>
    );
  }

  // Account exists — show details
  const needsOnboarding = !account.details_submitted;
  const availableBalance = balance?.available?.[0]?.amount || 0;
  const pendingBalance = balance?.pending?.[0]?.amount || 0;
  const reservedBalance = balance?.connect_reserved?.[0]?.amount || 0;
  const balanceCurrency = balance?.available?.[0]?.currency || account.default_currency || preferredCurrency;

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  // Build structured capabilities
  const capabilitiesMap = account.capabilities || {};
  const capabilityEntries = Object.entries(capabilitiesMap) as [string, string][];

  const capabilityLabels: Record<string, string> = {
    card_payments: 'Card Payments',
    transfers: 'Transfers',
    bank_transfer_payments: 'Bank Transfers',
    us_bank_account_ach_payments: 'ACH Payments',
    link_payments: 'Link Payments',
    tax_reporting_us_1099_k: '1099-K Tax Reporting',
    tax_reporting_us_1099_misc: '1099-MISC Tax Reporting',
  };

  const capabilityStatusColor: Record<string, string> = {
    active: 'green',
    inactive: 'gray',
    pending: 'yellow',
    unrequested: 'gray',
  };

  return (
    <div className="space-y-6 px-4 py-4">
      {/* Onboarding warning */}
      {needsOnboarding && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-start gap-3">
          <PiWarningCircleLight className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              {t('Onboarding Incomplete')}
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              {t('Complete your Stripe onboarding to start receiving payments. You need to verify your identity, add business details, and link a bank account.')}
            </p>
            <Button
              variant="warning"
              size="small"
              className="mt-2"
              onClick={handleConnect}
              isLoading={connecting}
            >
              {t('Complete Onboarding')}
            </Button>
          </div>
        </div>
      )}

      {/* Balance overview cards — matching Overview style */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 group cursor-pointer">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-black dark:text-white"><PiCurrencyDollarLight className="w-5 h-5" /></div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('Available Balance')}</span>
            </div>
            <p className="text-2xl font-normal text-gray-900 dark:text-white">
              {formatCurrency(availableBalance, balanceCurrency)}
            </p>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 group cursor-pointer">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-black dark:text-white"><PiClockLight className="w-5 h-5" /></div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('Pending Balance')}</span>
            </div>
            <p className="text-2xl font-normal text-gray-900 dark:text-white">
              {formatCurrency(pendingBalance, balanceCurrency)}
            </p>
          </div>
          {reservedBalance > 0 && (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 group cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-black dark:text-white"><PiShieldCheckLight className="w-5 h-5" /></div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('Reserved')}</span>
              </div>
              <p className="text-2xl font-normal text-gray-900 dark:text-white">
                {formatCurrency(reservedBalance, balanceCurrency)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Two-column layout: main content left, sidebar right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — Account Details + Verification + Capabilities */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account details card */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PiGearLight className="w-4 h-4 text-gray-500" />
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {t('Account Details')}
                </h3>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="small" onClick={handleRefresh}>
                  {t('Refresh')}
                </Button>
                {account.details_submitted && (
                  <Button variant="tertiary" size="small" onClick={handleOpenDashboard} icon={<PiArrowSquareOutLight className="w-4 h-4" />}>
                    {t('Stripe Dashboard')}
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DetailRow label={t('Status')}>
                <Badge color={account.status === 'active' ? 'green' : account.status === 'pending' ? 'yellow' : 'gray'}>
                  {account.status}
                </Badge>
              </DetailRow>

              <DetailRow label={t('Stripe Account ID')}>
                <span className="text-sm text-gray-900 dark:text-white font-mono">
                  {account.stripe_connect_account_id}
                </span>
              </DetailRow>

              <DetailRow label={t('Business Name')}>
                <span className="text-sm text-gray-900 dark:text-white">
                  {account.business_name || '—'}
                </span>
              </DetailRow>

              <DetailRow label={t('Country')}>
                <span className="text-sm text-gray-900 dark:text-white">
                  {account.country?.toUpperCase() || '—'}
                </span>
              </DetailRow>

              <DetailRow label={t('Default Currency')}>
                <span className="text-sm text-gray-900 dark:text-white uppercase">
                  {account.default_currency || '—'}
                </span>
              </DetailRow>

              <DetailRow label={t('Connected Since')}>
                <span className="text-sm text-gray-900 dark:text-white">
                  {account.created_at ? new Date(account.created_at).toLocaleDateString() : '—'}
                </span>
              </DetailRow>
            </div>
          </div>

          {/* Verification & Capabilities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Verification Status */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <PiShieldCheckLight className="w-4 h-4 text-gray-500" />
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {t('Verification Status')}
                </h3>
              </div>
              <div className="space-y-3">
                <VerificationItem
                  label={t('Identity Verified')}
                  verified={account.details_submitted}
                  description={account.details_submitted ? t('All required identity details have been submitted.') : t('Identity verification is required to process payments.')}
                />
                <VerificationItem
                  label={t('Payment Processing')}
                  verified={account.charges_enabled}
                  description={account.charges_enabled ? t('Your account can accept payments from clients.') : t('Payment processing is not yet enabled on this account.')}
                />
                <VerificationItem
                  label={t('Payouts Enabled')}
                  verified={account.payouts_enabled}
                  description={account.payouts_enabled ? t('Funds can be transferred to your bank account.') : t('Payouts are not yet enabled. Complete verification to receive payouts.')}
                />
              </div>
            </div>

            {/* Capabilities */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <PiInfoLight className="w-4 h-4 text-gray-500" />
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {t('Capabilities')}
                </h3>
              </div>
              {capabilityEntries.length > 0 ? (
                <div className="space-y-2">
                  {capabilityEntries.map(([key, status]) => (
                    <div key={key} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {capabilityLabels[key] || key.replace(/_/g, ' ')}
                      </span>
                      <Badge color={(capabilityStatusColor[status] || 'gray') as any}>
                        {status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <CapabilityItem label={t('Accept Payments')} enabled={account.charges_enabled} />
                  <CapabilityItem label={t('Payouts to Bank')} enabled={account.payouts_enabled} />
                  <CapabilityItem label={t('Details Submitted')} enabled={account.details_submitted} />
                </div>
              )}
              {account.details_submitted && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                  {t('Manage capabilities and request additional features from your')}{' '}
                  <button onClick={handleOpenDashboard} className="text-primary-600 hover:underline">
                    {t('Stripe Dashboard')}
                  </button>.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right column — Stripe Dashboard + Bank Account */}
        <div className="space-y-6">
          {/* Stripe Dashboard info */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <PiArrowSquareOutLight className="w-4 h-4 text-gray-500" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                {t('Stripe Dashboard')}
              </h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('What you can manage from your Stripe Dashboard:')}
            </p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <PiCheckCircleLight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('Update business & personal verification details')}
              </li>
              <li className="flex items-start gap-2">
                <PiCheckCircleLight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('Add or change bank accounts and payout schedules')}
              </li>
              <li className="flex items-start gap-2">
                <PiCheckCircleLight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('View detailed transaction history and reports')}
              </li>
              <li className="flex items-start gap-2">
                <PiCheckCircleLight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('Configure tax settings and download tax forms')}
              </li>
              <li className="flex items-start gap-2">
                <PiCheckCircleLight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('Manage disputes and refunds')}
              </li>
              <li className="flex items-start gap-2">
                <PiCheckCircleLight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('Set up automatic payouts (daily, weekly, or monthly)')}
              </li>
            </ul>
            {account.details_submitted && (
              <Button
                variant="primary"
                size="small"
                className="mt-4 w-full"
                onClick={handleOpenDashboard}
                icon={<PiArrowSquareOutLight className="w-4 h-4" />}
              >
                {t('Open Stripe Dashboard')}
              </Button>
            )}
          </div>

          {/* Bank account card */}
          {account.bank_last4 && (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <PiBankLight className="w-4 h-4 text-gray-500" />
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {t('Linked Bank Account')}
                </h3>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <PiBankLight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {account.bank_name || t('Bank Account')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    •••• {account.bank_last4} · {(account.default_currency || 'usd').toUpperCase()}
                  </p>
                </div>
              </div>
              {account.payouts_enabled && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Badge color="green">{t('Active')}</Badge>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
    {children}
  </div>
);

const CapabilityItem: React.FC<{ label: string; enabled: boolean }> = ({ label, enabled }) => (
  <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
    {enabled ? (
      <PiCheckCircleLight className="h-5 w-5 text-green-500" />
    ) : (
      <PiWarningCircleLight className="h-5 w-5 text-yellow-500" />
    )}
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </div>
);

const VerificationItem: React.FC<{ label: string; verified: boolean; description: string }> = ({ label, verified, description }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
    {verified ? (
      <PiCheckCircleLight className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
    ) : (
      <PiWarningCircleLight className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
    )}
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
    </div>
  </div>
);

export default BankAccountTab;
